import { UserAddOutlined } from '@ant-design/icons';
import { Button, Card, Divider, Empty, Select, Spin } from 'antd';
import { getMemberListV2 } from 'apis/ums';
import LOCALS from 'commons/locals';
import i18n from 'i18n';
import { debounce } from 'lodash-es';
import { User } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Control,
  Controller,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import { useToggle } from 'react-use';
import { CreateRecyclingConsignmentOrderV2SchemaType } from '../common';
import { MemberInfo as MemberInfoComponent } from './info';
import { getCityListByCountryCode } from 'apis/home';
import CreateMember from './create-member';

type MemberInfo = Awaited<ReturnType<typeof getMemberListV2>>['list'][number];

const useMemberSearchDebounced = () => {
  const [memberSearchLoading, toggleMemberSearchLoading] = useToggle(false);
  const [memberList, setMemberList] = useState<MemberInfo[]>([]);

  const debouncedSearch = useMemo(
    () =>
      debounce(async (keyword: string) => {
        keyword = keyword.trim();
        if (!keyword) {
          setMemberList([]);
          return;
        }
        toggleMemberSearchLoading(true);
        try {
          const data = await getMemberListV2({
            keyword,
            pageNum: 1,
            pageSize: 20,
          });
          setMemberList(data.list);
        } catch {
        } finally {
          toggleMemberSearchLoading(false);
        }
      }, 500),
    [toggleMemberSearchLoading],
  );

  return { memberSearchLoading, memberList, debouncedSearch };
};

const MemberSelect = ({
  control,
  setValue,
  initCreateSource,
}: {
  control: Control<CreateRecyclingConsignmentOrderV2SchemaType>;
  setValue: UseFormSetValue<CreateRecyclingConsignmentOrderV2SchemaType>;
  initCreateSource?: number;
}) => {
  const { memberSearchLoading, memberList, debouncedSearch } =
    useMemberSearchDebounced();
  const [createMemberOpen, toggleCreateMemberOpen] = useToggle(false);

  const memberId = useWatch({
    control,
    name: 'memberId',
  });
  const memberSearchValue = useWatch({
    control,
    name: 'memberSearchValue',
  });

  useEffect(() => {
    debouncedSearch(memberSearchValue);
  }, [debouncedSearch, memberSearchValue]);

  const onMemberChange = useCallback(
    ({
      firstName,
      lastName,
      countryCode,
      phone,
      country,
      city,
      detailAddress,
      email,
      paymentDetails,
    }: {
      firstName: string;
      lastName: string;
      countryCode: string;
      phone: string;
      country: string;
      city: string;
      detailAddress: string;
      email: string;
      paymentDetails: string;
    }) => {
      const name = firstName && lastName ? `${lastName} ${firstName}` : '';
      setValue('name', name);

      setValue('postCountryCode', countryCode || '');
      setValue('postPhone', phone || '');
      setValue('country', country || '');
      setValue('email', email || '');
      setValue('payoutDetail', paymentDetails || '');

      /**
       * 这里这样写，是因为 city 在用户表里存储的是 id
       * 但是在 logistics 表里存储的是 code
       */
      if (city) {
        if (country) {
          getCityListByCountryCode(country).then((res) => {
            const targetCity = res.data.cityList?.find((i) => i.id === city);
            if (targetCity) {
              setValue('city', targetCity.code);
            } else {
              setValue('city', '');
            }
          });
        } else {
          setValue('city', '');
        }
      } else {
        setValue('city', '');
      }

      setValue('detailAddress', detailAddress || '');
    },
    [setValue],
  );

  return (
    <Card bodyStyle={{ padding: '16px' }}>
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-base font-medium">
          <User className="w-5 h-5" />
          <div>{i18n.t(LOCALS.select_member)}</div>
        </div>

        <div className="flex gap-2 items-center">
          <Controller
            name="memberId"
            control={control}
            render={({ field, formState: { errors } }) => (
              <Select
                status={errors.memberId ? 'error' : undefined}
                showSearch
                filterOption={false}
                notFoundContent={
                  memberSearchLoading ? <Spin size="small" /> : <Empty />
                }
                className="w-full"
                placeholder={i18n.t(LOCALS.phone_or_email)}
                onSearch={(value) => {
                  setValue('memberSearchValue', value);
                }}
                {...field}
                onChange={(memberId) => {
                  field.onChange(memberId);
                  const target = memberList.find((i) => i.id === memberId);
                  if (target) {
                    onMemberChange({
                      firstName: target.firstName,
                      lastName: target.lastName,
                      countryCode: target.countryCode || '',
                      phone: target.phone,
                      country: target.country,
                      city: target.city || '',
                      detailAddress: target.detailAddress || '',
                      email: target.email,
                      paymentDetails: target.paymentDetails || '',
                    });
                  }
                }}
                options={memberList.map((i) => {
                  return {
                    value: i.id,
                    label: `${i.showName}(${i.email})`,
                  };
                })}
              ></Select>
            )}
          />

          <Button
            className="shrink-0"
            type="primary"
            onClick={() => {
              toggleCreateMemberOpen(true);
            }}
            icon={<UserAddOutlined />}
          >
            {i18n.t('add_member')}
          </Button>
        </div>

        <Divider />

        <MemberInfoComponent
          memberId={memberId}
          onSaveSuccess={onMemberChange}
        />
      </div>

      <CreateMember
        open={createMemberOpen}
        onClose={() => toggleCreateMemberOpen(false)}
        initCreateSource={initCreateSource}
        onSuccess={(data) => {
          setValue('memberId', data.id);
          setValue('memberSearchValue', data.email);
          onMemberChange({
            firstName: data.firstName,
            lastName: data.lastName,
            countryCode: data.countryCode || '',
            phone: data.phone || '',
            country: data.country,
            city: data.city || '',
            detailAddress: data.detailAddress || '',
            email: data.email,
            paymentDetails: data.paymentDetails || '',
          });
        }}
      />
    </Card>
  );
};

export default MemberSelect;
